'use client';

import {
  Children,
  type ComponentType,
  type Context,
  cloneElement,
  createContext,
  isValidElement,
  type ReactElement,
  type ReactNode,
  use,
} from 'react';

import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { ArrowRight01Icon, InformationCircleIconSolid } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet, type SilkSheetSize } from '@/components/ui/silk-sheet';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// InfoModal — composable wrapper for static info / how-it-works modals.
//
// Codifies the shape used across HoldingsInfo, GiftsHowItWorks,
// AnonymityDisclosure, Markets/Proposals/PrizePool HowItWorks,
// Referrals HowItWorks, and ChartHelp:
//
//   title + (X close button) + optional description + body (numbered steps
//   or icon rows) + optional callout + optional right-aligned footer.
//
// Thin wrapper over <SilkSheet variant="detached">. Subcomponents
// (Steps/Step/IconList/IconItem/Callout/Footer) are the single source of
// truth for each repeated fragment — no more 7× duplicated callout JSX.
// ---------------------------------------------------------------------------

export interface InfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title — required for ARIA. */
  title: ReactNode;
  /** Optional subtitle paragraph rendered below the title. */
  description?: ReactNode;
  /** Semantic size passthrough to SilkSheet. */
  size?: SilkSheetSize;
  /** Optional declarative trigger element. */
  trigger?: ReactNode;
  /** Render the built-in X close affordance. Default true. */
  closeButton?: boolean;
  /** Footer slot — use `<InfoModal.Footer>` for the canonical right-aligned shape. */
  footer?: ReactNode;
  /** Body content — wrap composable subcomponents (Steps, IconList, Callout). */
  children?: ReactNode;
  /** Override class on the body wrapper that vertically stacks children. */
  bodyClassName?: string;
}

interface InfoModalComponent {
  (props: InfoModalProps): React.ReactNode;
  Steps: ComponentType<StepsProps>;
  Step: ComponentType<StepProps>;
  IconList: ComponentType<IconListProps>;
  IconItem: ComponentType<IconItemProps>;
  Callout: ComponentType<CalloutProps>;
  CrossLink: ComponentType<CrossLinkProps>;
  DocsLink: ComponentType<DocsLinkProps>;
  Footer: ComponentType<FooterProps>;
}

function InfoModalImpl({
  open,
  onOpenChange,
  title,
  description,
  size = 'default',
  trigger,
  closeButton = true,
  footer,
  children,
  bodyClassName,
}: InfoModalProps): React.ReactNode {
  return (
    <SilkSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="detached"
      size={size}
      title={title}
      description={description}
      trigger={trigger}
      closeButton={closeButton}
      footer={footer}
    >
      <div className={cn('flex flex-col gap-4', bodyClassName)}>{children}</div>
    </SilkSheet>
  );
}

// ---------------------------------------------------------------------------
// Steps — numbered list. Variant controls badge style + connector lines.
// ---------------------------------------------------------------------------

interface StepsContextValue {
  total: number;
  connector: boolean;
  prominent: boolean;
}

const StepsContext: Context<StepsContextValue | null> = createContext<StepsContextValue | null>(
  null,
);

const StepIndexContext: Context<number> = createContext<number>(0);

export interface StepsProps {
  /** Render filled badge + vertical connector lines (chart-help style). When
   *  false (default) badges are subtle gray pills and there's no connector. */
  connector?: boolean;
  children: ReactNode;
}

function Steps({ connector = false, children }: StepsProps): React.ReactNode {
  const items = Children.toArray(children).filter(isValidElement) as ReactElement[];
  const ctx: StepsContextValue = { total: items.length, connector, prominent: connector };
  return (
    <StepsContext.Provider value={ctx}>
      <ol className={cn('flex flex-col', connector ? 'gap-0' : 'gap-3')}>
        {items.map((child, index) => (
          <StepIndexContext.Provider key={child.key} value={index}>
            {cloneElement(child)}
          </StepIndexContext.Provider>
        ))}
      </ol>
    </StepsContext.Provider>
  );
}

export interface StepProps {
  /** Optional bold label rendered above the body. */
  title?: ReactNode;
  /** Step body content. */
  children: ReactNode;
}

function Step({ title, children }: StepProps): React.ReactNode {
  const ctx = use(StepsContext);
  const index = use(StepIndexContext);
  if (ctx === null) {
    throw new Error('<InfoModal.Step> must be rendered inside <InfoModal.Steps>.');
  }
  const isLast = index === ctx.total - 1;
  const stepNumber = index + 1;

  if (ctx.prominent) {
    return (
      <li className="flex gap-4">
        <div className="flex flex-col items-center">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground-primary text-caption font-bold text-background-primary tabular-nums">
            {stepNumber}
          </div>
          {ctx.connector && !isLast ? <div className="mt-1 w-px flex-1 bg-border-primary" /> : null}
        </div>
        <div className={isLast ? 'pb-0' : 'pb-4'}>
          {title !== undefined && title !== null ? (
            <h3 className="text-heading-xs text-foreground-primary">{title}</h3>
          ) : null}
          <p className="mt-0.5 text-body text-foreground-secondary">{children}</p>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-background-tertiary text-caption font-medium text-foreground-primary tabular-nums"
      >
        {stepNumber}
      </span>
      {title !== undefined && title !== null ? (
        <div className="flex flex-col gap-1">
          <p className="text-body font-medium text-foreground-primary">{title}</p>
          <p className="text-caption text-foreground-secondary">{children}</p>
        </div>
      ) : (
        <p className="text-body text-foreground-secondary">{children}</p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// IconList / IconItem — icon-badge row pattern (replaces SectionRow).
// ---------------------------------------------------------------------------

export interface IconListProps {
  children: ReactNode;
}

function IconList({ children }: IconListProps): React.ReactNode {
  return <div className="flex flex-col gap-6">{children}</div>;
}

export interface IconItemProps {
  icon: IconSvgElement;
  title: ReactNode;
  description: ReactNode;
}

function IconItem({ icon, title, description }: IconItemProps): React.ReactNode {
  return (
    <div className="flex items-start gap-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-card bg-foreground-primary/10">
        <Icon icon={icon} className="size-5 text-foreground-primary" />
      </div>
      <div>
        <h3 className="text-heading-xs text-foreground-primary">{title}</h3>
        <p className="text-body text-foreground-secondary">{description}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Callout — single source of truth for the gray info box (was duplicated 7×).
// ---------------------------------------------------------------------------

export interface CalloutProps {
  /** Override the leading icon. Defaults to InformationCircleIconSolid. */
  icon?: IconSvgElement;
  children: ReactNode;
}

function Callout({ icon = InformationCircleIconSolid, children }: CalloutProps): React.ReactNode {
  return (
    <div className="flex items-start gap-2.5 rounded-overlay-item bg-background-tertiary px-3.5 py-3">
      <Icon icon={icon} className="mt-0.5 size-3.5 shrink-0 text-foreground-secondary" />
      <p className="text-caption text-foreground-secondary">{children}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CrossLink — underlined inline link that opens a focused sub-modal
// (e.g. Markets → "What is graduation?", Launchpad → "What is a proposal?").
// Rendered in the body after the IconList, before the Callout.
// ---------------------------------------------------------------------------

export interface CrossLinkProps {
  label: ReactNode;
  onClick: () => void;
}

const LINK_CLASS =
  'inline-flex items-center gap-1 text-body font-medium text-foreground-secondary underline underline-offset-4 hover:text-foreground-primary';

function CrossLink({ label, onClick }: CrossLinkProps): React.ReactNode {
  return (
    <AnimatedButton
      variant="tertiary"
      size="sm"
      onClick={onClick}
      suffix={ArrowRight01Icon}
      className={cn('h-auto self-start px-0 hover:bg-transparent', LINK_CLASS)}
    >
      {label}
    </AnimatedButton>
  );
}

// ---------------------------------------------------------------------------
// DocsLink — external "Read our docs" link, styled identically to CrossLink.
// Sits beside the CrossLink in the modal body (not in the footer).
// ---------------------------------------------------------------------------

export interface DocsLinkProps {
  href: string;
  label: ReactNode;
}

function DocsLink({ href, label }: DocsLinkProps): React.ReactNode {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('self-start', LINK_CLASS)}
    >
      {label}
      <Icon icon={ArrowRight01Icon} className="size-3.5" />
    </a>
  );
}

// ---------------------------------------------------------------------------
// Footer — right-aligned button group. Always primary CTA at the right;
// optional secondary action appears to its left.
// ---------------------------------------------------------------------------

export interface FooterAction {
  label: ReactNode;
  onClick: () => void;
}

export interface FooterProps {
  /** Primary CTA label. */
  cta: ReactNode;
  /** Primary CTA handler. Also fires when `primaryHref` is set, so consumers can
   *  use it to close the modal alongside navigation. */
  onClose: () => void;
  /** When provided, the primary CTA renders as an `<AnimatedButton asChild>` wrapping
   *  a `<Link>` to this route. `onClose` still fires on click so the modal dismisses. */
  primaryHref?: string;
  /** Optional secondary action rendered to the left of the primary CTA. */
  secondary?: FooterAction;
  /** Optional leading action: its own full-width row above the buttons on mobile,
   *  grown to fill the row beside them at sm+ (e.g. the explainer CTA beside
   *  "Got it"). Must accept `className` — `ModalFooter` sizes it like its siblings. */
  lead?: ReactNode;
}

function Footer({ cta, onClose, primaryHref, secondary, lead }: FooterProps): React.ReactNode {
  return (
    <ModalFooter className={cn(lead !== undefined && 'flex-wrap sm:flex-nowrap')}>
      {lead}
      {secondary !== undefined ? (
        <AnimatedButton variant="secondary" onClick={secondary.onClick}>
          {secondary.label}
        </AnimatedButton>
      ) : null}
      {primaryHref !== undefined ? (
        <AnimatedButton asChild>
          <Link href={primaryHref} onClick={onClose}>
            {cta}
          </Link>
        </AnimatedButton>
      ) : (
        <AnimatedButton onClick={onClose}>{cta}</AnimatedButton>
      )}
    </ModalFooter>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const InfoModal: InfoModalComponent = Object.assign(InfoModalImpl, {
  Steps,
  Step,
  IconList,
  IconItem,
  Callout,
  CrossLink,
  DocsLink,
  Footer,
});
